// ─── Group Card (IR-1228 P5) ───
//
// Rotem's spec (c61508): "Group card like the avatar card + Members (Dev/Prod)
// + Messages (Dev/Prod) + last message time".
//
// There was no group card. GroupEditDialog is an edit-only modal with four
// inputs and no status, members, messages, coverage or ingestion — a form, not
// a card. This is new, and deliberately a Sheet drawer like AvatarFormDialog so
// the two read alike, which is what "like the avatar card" means. It reuses
// that drawer's `.iris-field-label` convention, which the r4/r5 layout tests
// pinned after her own corrections: titles on .iris-field-label, values not.
//
// Read-only on purpose. Editing lives in GroupEditDialog; merging the two is
// the coherent end state but it changes an existing flow, so it needs her.
import { AvatarStopLines, LifecycleChip, LifecycleReason } from "@/components/iris";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import type { UnifiedGroupRecord } from "@/lib/types";

function fmt(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toISOString().slice(0, 10);
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <span className="iris-field-label">{label}</span>
      <span style={{ color: "var(--iris-fg-2)", fontSize: 13, wordBreak: "break-word" }}>
        {value ?? "—"}
      </span>
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        gap: 12,
      }}
    >
      {children}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <span
        style={{
          fontSize: 10,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: "var(--iris-fg-4)",
        }}
      >
        {title}
      </span>
      {children}
    </div>
  );
}

/** IR-1697 r10: an unanswered environment is Unknown, never a dash. */
function ingestionWord(value: string | null | undefined, label: string | null | undefined) {
  if (value === "could_not_check") return "Could Not Check";
  if (value === "not_applicable") return label ?? "Not Applicable";
  return label ?? "Unknown";
}

export function GroupCard({
  group,
  open,
  onClose,
}: {
  group: UnifiedGroupRecord | null;
  open: boolean;
  onClose: () => void;
}) {
  if (!open || !group) return null;

  // Per environment, never merged. prod and dev genuinely hold different data,
  // and a single number would hide that — the mistake the ingestion column made.
  const pair = (a: number | null | undefined, b: number | null | undefined) =>
    `${a ?? "—"} / ${b ?? "—"}`;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        style={{ width: 520, maxWidth: "100vw", overflowY: "auto", padding: 16 }}
      >
        <SheetTitle style={{ fontSize: 16 }}>{group.display_name ?? "—"}</SheetTitle>
        <SheetDescription className="iris-meta" style={{ fontSize: 11 }}>
          {group.group_application_type ?? "—"}
        </SheetDescription>

        {/* IR-1548: the card stays a SUMMARY. Everything that needs room —
            the per-session scraping folders, prod and dev interleaved, and the
            drill-down into each — lives on the full page rather than being
            squeezed into a 520px drawer and maintained twice.
            `group.id` is already the encrypted id (groups_unified encrypts it);
            a raw entity id here would be a regression. */}
        {/* A plain anchor, deliberately, not a TanStack <Link>: no feature
            component in this app uses client-side Link, and adding one here
            would force every existing GroupCard test to gain router context
            and become async. A full load on a deliberate "open the full
            dashboard" jump is an acceptable trade for that. */}
        <a
          href={`/group-dashboard/${group.id}`}
          style={{ fontSize: 12, display: "inline-block", marginTop: 8 }}
          className="text-[var(--iris-neon-blue)]"
        >
          Open full dashboard →
        </a>

        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 12 }}>
          <Section title="Identity">
            <Grid>
              <Field label="Group ID" value={group.scrapping_id ?? "—"} />
              {/* "Added to system" is entities.system_creation_time, served as
                  created_at — the same field the column uses (IR-1384 r4). */}
              <Field label="Added to system" value={fmt(group.created_at)} />
              <Field label="Added to Avatar" value={fmt(group.added_to_avatar)} />
            </Grid>
          </Section>

          {/* The four layers together. On the row they are narrow columns; here
              they sit side by side, which is the whole point of the model —
              one group, four independent questions. */}
          <Section title="Status">
            <Grid>
              {/* IR-1425 r10 (Rotem, ROUNDS 4): "Lifecycle", not "Status" — the
                  matrix LCP through the shared chip, with its reason and the
                  Quiet badge, so the card says exactly what the Groups row and
                  the Avatars expander say for this group. "—" until the backend
                  serves `lcp` (pre-port production); `prod_group_status_label`
                  is no longer rendered here (it stays in the type). */}
              <Field
                label="Lifecycle"
                value={
                  typeof group.lcp === "string" ? (
                    <span style={{ display: "inline-flex", flexDirection: "column", gap: 2 }}>
                      <LifecycleChip
                        lcp={group.lcp}
                        label={group.lcp_label}
                        color={group.lcp_color}
                        quiet={group.lcp_quiet}
                        quietWindowDays={7}
                      />
                      <LifecycleReason
                        lcp={group.lcp}
                        lcp_reason={group.lcp_reason}
                        lcp_reason_label={group.lcp_reason_label}
                        lcp_source={group.lcp_source}
                        lcp_set_at={group.lcp_set_at}
                        lcp_avatar_note={group.lcp_avatar_note}
                      />
                      <AvatarStopLines stop={group.avatar_stop} />
                    </span>
                  ) : (
                    "—"
                  )
                }
              />
              {/* IR-1465: the Collection value is production's. When dev is
                  receiving the group and production is not, say so here too —
                  the card is where someone goes to explain a row.
                  IR-1425 r10: the matrix Collection axis label when served;
                  today's merged label otherwise (Decision 4). */}
              <Field
                label="Collection"
                value={(() => {
                  const base = group.collection_axis_label ?? group.collection_lifecycle_label;
                  if (!base) return "—";
                  return group.dev_only_delivering ? `${base} · Dev only` : base;
                })()}
              />
              <Field label="Coverage" value={group.coverage_label ?? "—"} />
            </Grid>
            <Grid>
              <Field
                label="Ingestion"
                value={ingestionWord(group.prod_ingestion, group.prod_ingestion_label)}
              />
              <Field
                label="Ingestion (dev)"
                value={ingestionWord(group.dev_ingestion, group.dev_ingestion_label)}
              />
              <Field label="Last message" value={fmt(group.last_message_at)} />
            </Grid>
          </Section>

          <Section title="Volume (prod / dev)">
            <Grid>
              <Field label="Members" value={pair(group.prod_members, group.dev_members)} />
              <Field label="Messages" value={pair(group.prod_messages, group.dev_messages)} />
              <Field label="Avatars" value={group.avatars?.length ?? 0} />
            </Grid>
          </Section>
        </div>
      </SheetContent>
    </Sheet>
  );
}
